
import { computed, defineComponent, inject, ref, toRef, watch, watchEffect } from "vue";
import { SearchAutocompleteRequestBody, SearchResults } from "../services/services/apis/foxigy/search";
import { Services } from "../services/services";
import debounce from "lodash.debounce";
import { useSearchModuleStore } from "../store/searchModule";
import { onClickOutside, UseScrollReturn } from "@vueuse/core";
import { vScroll } from "@vueuse/components";
import { ApiResponse } from "../services/services/apis/foxigy/apiClient";
import {
    SearchAutocompleteProductsResponse,
    SearchAutocompleteCategoriesResponse
} from "../services/services/apis/foxigy/search";

interface ParsedSearchResults {
    data: SearchResults["data"];
    stats: SearchResults["stats"];
    search_after?: number[];
}

interface StoredSearchResults {
    products: ParsedSearchResults;
    categories: ParsedSearchResults;
}

export default defineComponent({
    directives: {
        scroll: vScroll
    },
    props: {
        criticalCss: String,
        afterCss: String,
        appCss: String,
        query: {
            type: String,
            default: ""
        },
        parent: Node
    },
    setup(props, { attrs, slots, emit, expose }) {
        const services = inject("services", new Services());
        const inputFocused = ref(false);
        const searchLoading = ref(false);
        const searchResults = ref<StoredSearchResults>({
            products: { data: [], stats: { total: 0 } },
            categories: { data: [], stats: { total: 0 } }
        });
        // const queryRef = toRef(props, "query", "");

        const getSearchData = debounce(
            async (
                entity_types: Array<"product" | "category">,
                query: string,
                isPaginationRequest: boolean = false
            ) => {
                const loadPromises = [];
                searchLoading.value = true;

                for (const entity_type of entity_types) {
                    let storeKey: "categories" | "products" = "products";
                    if (entity_type === "category") {
                        storeKey = "categories";
                    }

                    loadPromises.push(
                        storeLodedData(
                            storeKey,
                            Promise.resolve({ data: [], stats: { total: 0 } }),
                            isPaginationRequest
                        )
                    );
                    if (query !== "") {
                        const requestBody: SearchAutocompleteRequestBody = {
                            language: services.getLanguage(),
                            query
                        };
                        if (isPaginationRequest) {
                            requestBody.search_after = searchResults.value[storeKey].search_after;
                        }
                        const response = services.searchApiService().searchAutocomplete(entity_type, {}, requestBody);
                        const parsedData = parseLoadedData(response);
                        loadPromises.push(storeLodedData(storeKey, parsedData, isPaginationRequest));
                    }
                }
                await Promise.all(loadPromises);
                searchLoading.value = false;
            },
            300
        );

        const parseLoadedData = async (
            data: Promise<ApiResponse<SearchAutocompleteProductsResponse | SearchAutocompleteCategoriesResponse>>
        ) => {
            const loadedData = (await data).response?.data?.searchResults?.data ?? [];
            const parsedData: ParsedSearchResults = { data: [], stats: { total: 0 } };
            if (loadedData.length) {
                parsedData.data = loadedData;

                parsedData.stats = (await data).response?.data?.searchResults?.stats || { total: 0 };
                parsedData.search_after = loadedData[loadedData.length - 1].sort;
            }
            return parsedData;
        };

        const storeLodedData = async (
            storeKey: "products" | "categories",
            parsedData: Promise<ParsedSearchResults>,
            isPaginationRequest: boolean
        ) => {
            if (isPaginationRequest) {
                searchResults.value[storeKey].data = searchResults.value[storeKey].data.concat((await parsedData).data);
            } else {
                searchResults.value[storeKey].data = (await parsedData).data;
            }
            searchResults.value[storeKey].search_after = (await parsedData).search_after;
            searchResults.value[storeKey].stats = (await parsedData).stats;
            return searchResults.value[storeKey];
        };

        // const categoriesSearchResultsElement = ref<HTMLElement>();

        const loadMoreCategories = function (state: UseScrollReturn) {
            // console.log("load more categories", state);
            // load more
            if (
                state.arrivedState.bottom &&
                searchResults.value.categories.stats.total > searchResults.value.categories.data.length
            ) {
                getSearchData(["category"], props.query, true);
            }
        };

        // const productsSearchResultsElement = ref<HTMLElement>();
        const loadMoreProducts = function (state: UseScrollReturn) {
            // console.log(
            //     "load more products",
            //     searchResults.value.products.stats,
            //     searchResults.value.products.data.length,
            //     state
            // );
            // load more
            if (
                state.arrivedState.bottom &&
                searchResults.value.products.stats.total > searchResults.value.products.data.length
            ) {
                getSearchData(["product"], props.query, true);
            }
        };

        const search = (query: string) => {
            // console.log("search");

            getSearchData(["product", "category"], query);
        };

        watchEffect(() => {
            search(props.query);
        });

        const productsContainer = ref<HTMLElement | null>(null);
        // useResizeObserver(productsContainer, entries => {
        //     const entry = entries[0];
        //     const { width, height } = entry.contentRect;
        //     console.log(width, height);

        // });

        // const { x, y, isScrolling, arrivedState, directions } = useScroll(productsContainer, {
        //     behavior: "smooth",
        //     onScroll: () => {
        //         console.log(x, y, isScrolling, arrivedState, directions);
        //     }
        // });

        watchEffect(() => {});

        const searchModuleStore = useSearchModuleStore();
        const toggleResultsVisibility = () => {
            searchModuleStore.toggleResultsVisibility();
        };

        const rootElement = ref<HTMLElement>();
        onClickOutside(
            rootElement,
            function (evt) {
                // console.log("onClickOutside evt.target", evt.target);
                // console.log("props.parent", props.parent);
                if (
                    evt.target instanceof Node &&
                    (evt.target as HTMLElement)?.shadowRoot?.contains(props?.parent || null)
                ) {
                } else {
                    searchModuleStore.setResultsVisibility(false);
                }
            },
            { ignore: [] }
        );

        const dataNotEmpty = computed(() => {
            let result = false;

            if (searchResults.value.products.data.length || searchResults.value.categories.data.length) {
                result = true;
            } else {
                result = false;
            }
            return result;
        });

        const shouldDisplaySearchDialog = computed(() => {
            let result = false;

            if (dataNotEmpty.value && searchModuleStore.getResultsVisibility) {
                result = true;
            } else {
                result = false;
            }
            return result;
        });
        watch(shouldDisplaySearchDialog, () => {
            console.log("body overflow change");
            const bodyElement: HTMLElement | null = document.querySelector(
                ".d-flex.flex-column.min-h-100vh.bg-white.bg-img-s.bg-fixed-full.layout-4.layout-fluid.layout-col-two.layout-col-left.bg-header-pos-1.lang-sk.device-desktop.page-hp.is-bnr-main.vs.is-top-menu.is-left-menu.is-bottom-menu.bg-size-contain.bg-v-left-top.screen-landscape.noTouch"
            );
            if (shouldDisplaySearchDialog.value) {
                if (bodyElement) {
                    bodyElement.style.overflowY = "hidden";
                }
            } else {
                if (bodyElement) {
                    bodyElement.style.overflowY = "auto";
                }
            }
        });

        const baseImageUrl = process.env.VUE_APP_IMAGE_BASE_URL;

        return {
            props,
            rootElement,
            inputFocused,
            searchResults,
            searchLoading,
            toggleResultsVisibility,
            searchModuleStore,
            dataNotEmpty,
            shouldDisplaySearchDialog,
            loadMoreProducts,
            loadMoreCategories,
            baseImageUrl
        };
    }
});
